import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { getApiBase, getAuthHeaders } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { useParadigm } from '../contexts/ParadigmContext';

// Initialize Stripe
const stripePublishableKey =
  process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY ||
  process.env.REACT_APP_STRIPE_TEST_PUBLISHABLE_KEY ||
  'pk_test_placeholder';
const stripePromise = loadStripe(stripePublishableKey);

// Card element styling
const cardElementOptions = {
  style: {
    base: {
      fontSize: '16px',
      color: '#424770',
      '::placeholder': {
        color: '#aab7c4',
      },
    },
    invalid: {
      color: '#9e2146',
    },
  },
};

/**
 * CardEntryForm - Stripe card collection form
 */
const CardEntryForm = ({ onSuccess, onCancel, isSubmitting, setIsSubmitting, setError }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [cardError, setCardError] = useState('');
  const [cardComplete, setCardComplete] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setIsSubmitting(true);
    setCardError('');
    setError('');

    try {
      const cardElement = elements.getElement(CardElement);

      // Create payment method directly
      const { error, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
      });

      if (error) {
        setCardError(error.message);
        setIsSubmitting(false);
        return;
      }

      // Pass the payment method ID back to parent
      onSuccess(paymentMethod.id);
    } catch (err) {
      setCardError('Failed to process card. Please try again.');
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Card Details
        </label>
        <div className="p-3 border border-gray-300 rounded-lg bg-white">
          <CardElement
            options={cardElementOptions}
            onChange={(e) => {
              setCardComplete(e.complete);
              if (e.error) {
                setCardError(e.error.message);
              } else {
                setCardError('');
              }
            }}
          />
        </div>
        {cardError && (
          <p className="mt-1 text-sm text-red-600">{cardError}</p>
        )}
      </div>

      <div className="flex space-x-3">
        <button
          type="button"
          onClick={onCancel}
          disabled={isSubmitting}
          className="flex-1 bg-white text-gray-700 py-2 px-4 rounded-lg font-medium border border-gray-300 hover:bg-gray-50 transition-colors disabled:opacity-50"
        >
          Back
        </button>
        <button
          type="submit"
          disabled={isSubmitting || !stripe || !cardComplete}
          className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
        >
          {isSubmitting ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              Processing...
            </>
          ) : (
            'Approve & Pay'
          )}
        </button>
      </div>

      <p className="text-xs text-gray-500 text-center">
        Your card will be securely saved and charged as shown in the billing summary above.
        You can cancel your subscription at any time from your billing settings.
      </p>
    </form>
  );
};

/**
 * SavedCardSelector - Component to select from saved payment methods
 */
const SavedCardSelector = ({ savedCards, selectedCardId, onSelect, onUseNewCard }) => {
  const getCardIcon = (brand) => {
    const brandLower = (brand || '').toLowerCase();
    if (brandLower === 'visa') {
      return '💳 Visa';
    } else if (brandLower === 'mastercard') {
      return '💳 Mastercard';
    } else if (brandLower === 'amex' || brandLower === 'american express') {
      return '💳 Amex';
    } else if (brandLower === 'discover') {
      return '💳 Discover';
    }
    return '💳';
  };

  return (
    <div className="space-y-3">
      <label className="block text-sm font-medium text-gray-700">
        Select Payment Method
      </label>

      {/* Saved cards */}
      {savedCards.map((card) => (
        <div
          key={card.id}
          onClick={() => onSelect(card.stripe_payment_method_id)}
          className={`p-3 border rounded-lg cursor-pointer transition-colors ${
            selectedCardId === card.stripe_payment_method_id
              ? 'border-blue-500 bg-blue-50'
              : 'border-gray-300 hover:border-blue-300'
          }`}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <input
                type="radio"
                name="paymentMethod"
                checked={selectedCardId === card.stripe_payment_method_id}
                onChange={() => onSelect(card.stripe_payment_method_id)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500"
              />
              <span className="ml-3 font-medium">
                {getCardIcon(card.brand)} •••• {card.last_four}
              </span>
            </div>
            <span className="text-sm text-gray-500">
              Expires {String(card.exp_month).padStart(2, '0')}/{card.exp_year}
            </span>
          </div>
          {card.is_default && (
            <span className="ml-7 text-xs text-blue-600">Default</span>
          )}
        </div>
      ))}

      {/* New card option */}
      <div
        onClick={onUseNewCard}
        className={`p-3 border rounded-lg cursor-pointer transition-colors ${
          selectedCardId === 'new'
            ? 'border-blue-500 bg-blue-50'
            : 'border-gray-300 hover:border-blue-300'
        }`}
      >
        <div className="flex items-center">
          <input
            type="radio"
            name="paymentMethod"
            checked={selectedCardId === 'new'}
            onChange={onUseNewCard}
            className="h-4 w-4 text-blue-600 focus:ring-blue-500"
          />
          <span className="ml-3 font-medium">
            + Use a new card
          </span>
        </div>
      </div>
    </div>
  );
};

/**
 * BillingProfileForm - Form for reviewing/completing billing profile
 */
const BillingProfileForm = ({ billingProfile, onChange, errors }) => {
  const handleFieldChange = (field, value) => {
    onChange({ ...billingProfile, [field]: value });
  };

  const handleAddressChange = (field, value) => {
    onChange({
      ...billingProfile,
      billing_address: {
        ...billingProfile.billing_address,
        [field]: value
      }
    });
  };

  return (
    <div className="space-y-4">
      <h3 className="font-semibold text-gray-900">Billing Information</h3>
      <p className="text-sm text-gray-600">Please review and complete your billing information.</p>

      {/* Company Name */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Company Name <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          value={billingProfile.company_name || ''}
          onChange={(e) => handleFieldChange('company_name', e.target.value)}
          className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
            errors.company_name ? 'border-red-500' : 'border-gray-300'
          }`}
          placeholder="Enter company name"
        />
        {errors.company_name && (
          <p className="mt-1 text-sm text-red-600">{errors.company_name}</p>
        )}
      </div>

      {/* Billing Email */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Billing Email <span className="text-red-500">*</span>
        </label>
        <input
          type="email"
          value={billingProfile.billing_email || ''}
          onChange={(e) => handleFieldChange('billing_email', e.target.value)}
          className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
            errors.billing_email ? 'border-red-500' : 'border-gray-300'
          }`}
          placeholder="billing@company.com"
        />
        {errors.billing_email && (
          <p className="mt-1 text-sm text-red-600">{errors.billing_email}</p>
        )}
      </div>

      {/* Billing Phone */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Billing Phone
        </label>
        <input
          type="tel"
          value={billingProfile.billing_phone || ''}
          onChange={(e) => handleFieldChange('billing_phone', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          placeholder="+1 (555) 123-4567"
        />
      </div>

      {/* Tax ID */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Tax ID / EIN
        </label>
        <input
          type="text"
          value={billingProfile.tax_id || ''}
          onChange={(e) => handleFieldChange('tax_id', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          placeholder="XX-XXXXXXX"
        />
      </div>

      {/* Address Section */}
      <div className="border-t pt-4 mt-4">
        <h4 className="font-medium text-gray-900 mb-3">Billing Address</h4>

        {/* Street Address */}
        <div className="mb-3">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Street Address <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={billingProfile.billing_address?.line1 || ''}
            onChange={(e) => handleAddressChange('line1', e.target.value)}
            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
              errors.line1 ? 'border-red-500' : 'border-gray-300'
            }`}
            placeholder="123 Main Street"
          />
          {errors.line1 && (
            <p className="mt-1 text-sm text-red-600">{errors.line1}</p>
          )}
        </div>

        {/* Address Line 2 */}
        <div className="mb-3">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Address Line 2
          </label>
          <input
            type="text"
            value={billingProfile.billing_address?.line2 || ''}
            onChange={(e) => handleAddressChange('line2', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Suite 100"
          />
        </div>

        {/* City and State */}
        <div className="grid grid-cols-2 gap-3 mb-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              City <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={billingProfile.billing_address?.city || ''}
              onChange={(e) => handleAddressChange('city', e.target.value)}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                errors.city ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="City"
            />
            {errors.city && (
              <p className="mt-1 text-sm text-red-600">{errors.city}</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              State / Province
            </label>
            <input
              type="text"
              value={billingProfile.billing_address?.state || ''}
              onChange={(e) => handleAddressChange('state', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="State"
            />
          </div>
        </div>

        {/* Postal Code and Country */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Postal Code <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={billingProfile.billing_address?.postal_code || ''}
              onChange={(e) => handleAddressChange('postal_code', e.target.value)}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                errors.postal_code ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="12345"
            />
            {errors.postal_code && (
              <p className="mt-1 text-sm text-red-600">{errors.postal_code}</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Country <span className="text-red-500">*</span>
            </label>
            <select
              value={billingProfile.billing_address?.country || 'US'}
              onChange={(e) => handleAddressChange('country', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="US">United States</option>
              <option value="CA">Canada</option>
              <option value="GB">United Kingdom</option>
              <option value="AU">Australia</option>
              <option value="DE">Germany</option>
              <option value="FR">France</option>
              <option value="Other">Other</option>
            </select>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * PackageEntitlements - Display package features and entitlements
 */
const PackageEntitlements = ({ entitlements }) => {
  const { labels } = useParadigm();
  if (!entitlements) return null;

  const formatLimit = (config, label) => {
    if (!config) return null;
    const { limit, period, per_executive } = config;
    if (period === 'unlimited') return 'Unlimited';
    const perText = per_executive ? ' per executive' : '';
    const periodText = period === 'month' ? '/month' : period === 'year' ? '/year' : '';
    return `${limit}${periodText}${perText}`;
  };

  const formatUserLimit = (config, type) => {
    if (!config) return 'Not available';
    const { limit_type, included_count, max_count, cost_per_additional } = config;

    if (limit_type === 'unlimited') {
      return 'Unlimited';
    } else if (limit_type === 'fixed') {
      return max_count ? `Up to ${max_count}` : 'Contact for details';
    } else if (limit_type === 'included_plus_extra') {
      const included = included_count || 0;
      const costText = cost_per_additional > 0
        ? ` (+$${cost_per_additional}/additional)`
        : ' (additional available)';
      return `${included} included${costText}`;
    }
    return 'Contact for details';
  };

  const FeatureItem = ({ enabled, label, detail }) => {
    // Only show features that are included
    if (!enabled) return null;

    return (
      <div className="flex items-center py-1">
        <svg className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
        <span className="text-gray-700">
          {label}
          {detail && <span className="text-gray-500 text-xs ml-1">({detail})</span>}
        </span>
      </div>
    );
  };

  const features = entitlements.features || {};

  return (
    <div className="space-y-4">
      {/* Package Description */}
      {entitlements.description && (
        <p className="text-sm text-gray-600 italic">{entitlements.description}</p>
      )}

      {/* Users & Seats */}
      <div className="bg-blue-50 rounded-lg p-3">
        <h4 className="font-medium text-blue-900 mb-2 flex items-center">
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
          </svg>
          Users & Seats
        </h4>
        <div className="space-y-1 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-600">Executives:</span>
            <span className="font-medium">{formatUserLimit(entitlements.executives, 'executive')}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">{labels.executiveAssistants}:</span>
            <span className="font-medium">{formatUserLimit(entitlements.executive_assistants, 'ea')}</span>
          </div>
        </div>
      </div>

      {/* Available Components */}
      {entitlements.available_components && entitlements.available_components.length > 0 && (
        <div>
          <h4 className="font-medium text-gray-900 mb-2 flex items-center">
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
            </svg>
            Itinerary Components
          </h4>
          <div className="flex flex-wrap gap-2">
            {entitlements.available_components.map((component, idx) => (
              <span
                key={idx}
                className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800"
              >
                {component}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Core Features */}
      {(features.todos_enabled || features.sticky_notes_enabled || features.teams_enabled || features.flight_verification_enabled) && (
        <div>
          <h4 className="font-medium text-gray-900 mb-2 flex items-center">
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
            </svg>
            Core Features
          </h4>
          <div className="grid grid-cols-2 gap-x-4 text-sm">
            <FeatureItem enabled={features.todos_enabled} label="Task Management" />
            <FeatureItem enabled={features.sticky_notes_enabled} label="Sticky Notes" />
            <FeatureItem enabled={features.teams_enabled} label="Team Collaboration" />
            <FeatureItem
              enabled={features.flight_verification_enabled}
              label="Flight Verification"
            />
          </div>
        </div>
      )}

      {/* Flight Alerts */}
      {features.flight_alerts?.enabled && (
        <div className="bg-green-50 rounded-lg p-3">
          <div className="flex items-center text-green-800">
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
            </svg>
            <span className="font-medium">Flight Alerts Included</span>
            <span className="ml-2 text-sm">({formatLimit(features.flight_alerts, 'alerts')})</span>
          </div>
        </div>
      )}

      {/* Document Parsing */}
      {features.document_parsing && (
        <div className="text-sm">
          <div className="flex items-center text-gray-700">
            <svg className="w-4 h-4 mr-2 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <span>Document Parsing: </span>
            <span className="font-medium ml-1">{formatLimit(features.document_parsing, 'documents')}</span>
          </div>
        </div>
      )}

      {/* Email Features */}
      {(features.dedicated_emails_executives || features.dedicated_emails_eas) && (
        <div>
          <h4 className="font-medium text-gray-900 mb-2 flex items-center">
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            Email Features
          </h4>
          <div className="text-sm space-y-1">
            <FeatureItem enabled={features.dedicated_emails_executives} label="Dedicated email addresses for executives" />
            <FeatureItem enabled={features.dedicated_emails_eas} label="Dedicated email addresses for EAs" />
          </div>
        </div>
      )}

      {/* Integrations */}
      {features.urbanride_quickbook_enabled && (
        <div>
          <h4 className="font-medium text-gray-900 mb-2 flex items-center">
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 4a2 2 0 114 0v1a1 1 0 001 1h3a1 1 0 011 1v3a1 1 0 01-1 1h-1a2 2 0 100 4h1a1 1 0 011 1v3a1 1 0 01-1 1h-3a1 1 0 01-1-1v-1a2 2 0 10-4 0v1a1 1 0 01-1 1H7a1 1 0 01-1-1v-3a1 1 0 00-1-1H4a2 2 0 110-4h1a1 1 0 001-1V7a1 1 0 011-1h3a1 1 0 001-1V4z" />
            </svg>
            Integrations
          </h4>
          <div className="text-sm space-y-1">
            <FeatureItem enabled={features.urbanride_quickbook_enabled} label="Urbanride one-click chauffeur booking" />
          </div>
        </div>
      )}

      {/* Enterprise & Security */}
      {(features.sso_enabled || features.saml_sso_enabled || features.oidc_sso_enabled ||
        features.audit_logs_enabled || features.rbac_enabled) && (
        <div>
          <h4 className="font-medium text-gray-900 mb-2 flex items-center">
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
            Enterprise & Security
          </h4>
          <div className="text-sm space-y-1">
            <FeatureItem enabled={features.sso_enabled || features.saml_sso_enabled || features.oidc_sso_enabled} label="Single Sign-On (SSO)" />
            {features.saml_sso_enabled && <FeatureItem enabled={true} label="SAML 2.0 Enterprise SSO" />}
            {features.oidc_sso_enabled && <FeatureItem enabled={true} label="OpenID Connect (OIDC) SSO" />}
            <FeatureItem enabled={features.audit_logs_enabled} label="Audit Logs" />
            <FeatureItem enabled={features.rbac_enabled} label="Role-Based Access Control (RBAC)" />
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * LegalDocumentCard - Component to display a legal document with acceptance checkbox
 */
const LegalDocumentCard = ({ document, accepted, onAcceptChange, onViewFullDocument }) => {
  const [expanded, setExpanded] = useState(false);
  const [showFullModal, setShowFullModal] = useState(false);

  // Function to open document in new window for printing/download
  const handleDownload = () => {
    const content = document.content_html || document.content_markdown || '';
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <title>${document.name} - v${document.version}</title>
          <style>
            body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; max-width: 800px; margin: 40px auto; padding: 20px; line-height: 1.6; }
            h1 { color: #1a1a1a; border-bottom: 2px solid #3b82f6; padding-bottom: 10px; }
            h2 { color: #374151; margin-top: 24px; }
            h3 { color: #4b5563; }
            p { color: #374151; }
            ul, ol { color: #374151; }
            .version { color: #6b7280; font-size: 14px; }
            .effective-date { color: #6b7280; font-size: 14px; margin-bottom: 20px; }
            @media print { body { margin: 0; padding: 20px; } }
          </style>
        </head>
        <body>
          <h1>${document.name}</h1>
          <p class="version">Version ${document.version}</p>
          ${document.effective_date ? `<p class="effective-date">Effective Date: ${new Date(document.effective_date).toLocaleDateString()}</p>` : ''}
          ${document.content_html || `<pre style="white-space: pre-wrap; font-family: inherit;">${document.content_markdown || ''}</pre>`}
        </body>
        </html>
      `);
      printWindow.document.close();
    }
  };

  return (
    <>
      <div className="border border-gray-200 rounded-lg overflow-hidden">
        {/* Header */}
        <div className="bg-gray-50 px-4 py-3 flex items-center justify-between">
          <div className="flex items-center">
            <svg className="w-5 h-5 text-gray-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <span className="font-medium text-gray-900">{document.name}</span>
            <span className="ml-2 text-xs text-gray-500">v{document.version}</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setShowFullModal(true)}
              className="text-blue-600 text-sm hover:text-blue-800 flex items-center"
              title="View full document"
            >
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
              View
            </button>
            <button
              type="button"
              onClick={handleDownload}
              className="text-gray-600 text-sm hover:text-gray-800 flex items-center"
              title="Download/Print document"
            >
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              Download
            </button>
          </div>
        </div>

        {/* Key Points Summary */}
        {document.key_points && document.key_points.length > 0 && (
          <div className="px-4 py-2 bg-blue-50 border-t border-gray-200">
            <p className="text-xs font-medium text-blue-800 mb-1">Key Points:</p>
            <ul className="text-xs text-blue-700 space-y-0.5">
              {document.key_points.slice(0, 5).map((point, idx) => (
                <li key={idx} className="flex items-start">
                  <svg className="w-3 h-3 text-blue-500 mr-1 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  {point}
                </li>
              ))}
              {document.key_points.length > 5 && (
                <li className="text-blue-600 cursor-pointer" onClick={() => setShowFullModal(true)}>
                  + {document.key_points.length - 5} more...
                </li>
              )}
            </ul>
          </div>
        )}

        {/* Acceptance Checkbox */}
        <div className="px-4 py-3 bg-white border-t border-gray-200">
          <label className="flex items-start cursor-pointer">
            <input
              type="checkbox"
              checked={accepted}
              onChange={(e) => onAcceptChange(document.id, e.target.checked)}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mt-0.5"
            />
            <span className="ml-2 text-sm text-gray-700">
              {document.checkbox_text || `I have read and agree to the ${document.name}`}
            </span>
            {document.is_mandatory && (
              <span className="ml-1 text-red-500">*</span>
            )}
          </label>
        </div>
      </div>

      {/* Full Document Modal */}
      {showFullModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] flex flex-col">
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">{document.name}</h3>
                <p className="text-sm text-gray-500">Version {document.version}
                  {document.effective_date && ` | Effective: ${new Date(document.effective_date).toLocaleDateString()}`}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={handleDownload}
                  className="inline-flex items-center px-3 py-1.5 text-sm text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                >
                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  Download
                </button>
                <button
                  type="button"
                  onClick={() => setShowFullModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Modal Content */}
            <div className="px-6 py-4 overflow-y-auto flex-grow">
              {document.summary && (
                <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-600 italic">{document.summary}</p>
                </div>
              )}

              {document.key_points && document.key_points.length > 0 && (
                <div className="mb-4 p-3 bg-blue-50 rounded-lg">
                  <p className="text-sm font-medium text-blue-800 mb-2">Key Points:</p>
                  <ul className="text-sm text-blue-700 space-y-1">
                    {document.key_points.map((point, idx) => (
                      <li key={idx} className="flex items-start">
                        <svg className="w-4 h-4 text-blue-500 mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        {point}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Full Document Content */}
              <div className="prose prose-sm max-w-none">
                {document.content_html ? (
                  <div dangerouslySetInnerHTML={{ __html: document.content_html }} />
                ) : document.content_markdown ? (
                  <pre className="whitespace-pre-wrap font-sans text-sm text-gray-700">{document.content_markdown}</pre>
                ) : (
                  <p className="text-gray-500 italic">Document content not available.</p>
                )}
              </div>
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end flex-shrink-0">
              <button
                type="button"
                onClick={() => setShowFullModal(false)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

/**
 * BillingSummary - Clear breakdown of billing charges and cycle
 * Shows what will be charged today, recurring charges, and billing schedule
 * Uses backend-provided billing_summary for accurate proration calculations
 */
const BillingSummary = ({ subscription, billingSummary, existingSubscription, showConfirmation = false }) => {
  if (!subscription || !billingSummary) return null;

  const formatPrice = (cents) => {
    if (cents === 0 || cents === null || cents === undefined) return '$0.00';
    return `$${(cents / 100).toFixed(2)}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Use billing summary data from backend
  const todaysCharge = billingSummary.todays_charge || 0;
  const todaysChargeExplanation = billingSummary.todays_charge_explanation || '';
  const isUpgrade = billingSummary.is_upgrade || false;
  const isDowngrade = billingSummary.is_downgrade || false;
  const prorationCredit = billingSummary.proration_credit || 0;
  const prorationCharge = billingSummary.proration_charge || 0;
  const nextBillingDate = billingSummary.next_billing_date;
  const nextBillingAmount = billingSummary.next_billing_amount || 0;
  const recurringAmount = billingSummary.recurring_amount || subscription.effective_price || 0;
  const recurringInterval = billingSummary.recurring_interval || subscription.billing_interval || 'monthly';
  const message = billingSummary.message;

  // Promo info
  const isFreeTrial = billingSummary.is_free_trial || subscription.promo_type === 'free_trial';
  const isDiscounted = billingSummary.is_discounted || subscription.promo_type === 'discounted';
  const promoEndDate = billingSummary.promo_end_date || subscription.promo_end_date;
  const promoDurationDays = billingSummary.promo_duration_days || subscription.promo_duration_days;
  const promoDiscountPercent = billingSummary.promo_discount_percent || subscription.promo_discount_percent;

  // Downgrade specific
  const scheduledChangeDate = billingSummary.scheduled_change_date;
  const currentPlanContinuesUntil = billingSummary.current_plan_continues_until;

  // Determine interval labels
  const intervalLabel = recurringInterval === 'yearly' ? 'year' : 'month';
  const intervalLabelFull = recurringInterval === 'yearly' ? 'Annually' : 'Monthly';

  // Determine today's charge label
  let todaysChargeLabel = "Today's Charge";
  if (isFreeTrial) {
    todaysChargeLabel = "Due Today (Free Trial)";
  } else if (isUpgrade) {
    todaysChargeLabel = "Today's Charge (Prorated Upgrade)";
  } else if (isDowngrade) {
    todaysChargeLabel = "Due Today";
  }

  return (
    <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4 mb-4">
      <h3 className="font-semibold text-gray-900 mb-4 flex items-center">
        <svg className="w-5 h-5 mr-2 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
        </svg>
        Billing Summary
      </h3>

      {/* Upgrade/Downgrade Badge */}
      {(isUpgrade || isDowngrade) && existingSubscription && (
        <div className={`mb-3 p-2 rounded-lg text-sm ${isUpgrade ? 'bg-green-50 border border-green-200' : 'bg-orange-50 border border-orange-200'}`}>
          <div className="flex items-center">
            <svg className={`w-4 h-4 mr-2 ${isUpgrade ? 'text-green-600' : 'text-orange-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {isUpgrade ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
              )}
            </svg>
            <span className={`font-medium ${isUpgrade ? 'text-green-800' : 'text-orange-800'}`}>
              {isUpgrade ? 'Upgrading' : 'Downgrading'} from {existingSubscription.package_name} ({formatPrice(existingSubscription.effective_price)}/{existingSubscription.billing_interval})
            </span>
          </div>
        </div>
      )}

      {/* Today's Charge Box */}
      <div className="bg-white rounded-lg p-4 mb-4 border border-blue-100">
        <div className="flex justify-between items-center mb-2">
          <span className="text-gray-600 text-sm">{todaysChargeLabel}</span>
          <span className={`text-2xl font-bold ${todaysCharge === 0 ? 'text-green-600' : 'text-blue-600'}`}>
            {formatPrice(todaysCharge)}
          </span>
        </div>

        {/* Explanation text */}
        {todaysChargeExplanation && (
          <p className="text-xs text-gray-500 mb-2">{todaysChargeExplanation}</p>
        )}

        {/* Proration breakdown for upgrades */}
        {isUpgrade && prorationCredit > 0 && (
          <div className="text-xs text-gray-500 border-t border-gray-100 pt-2 mt-2 space-y-1">
            <div className="flex justify-between">
              <span>Prorated new plan ({billingSummary.days_remaining} days):</span>
              <span>{formatPrice(prorationCharge)}</span>
            </div>
            <div className="flex justify-between text-green-600">
              <span>Credit from current plan:</span>
              <span>-{formatPrice(prorationCredit)}</span>
            </div>
            <div className="flex justify-between font-medium border-t border-gray-200 pt-1 mt-1">
              <span>Net charge today:</span>
              <span>{formatPrice(todaysCharge)}</span>
            </div>
          </div>
        )}

        {/* Downgrade notice */}
        {isDowngrade && (
          <div className="flex items-center text-orange-700 text-sm mt-2">
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Your current plan continues until {formatDate(currentPlanContinuesUntil)}
          </div>
        )}

        {/* Free trial notice */}
        {isFreeTrial && (
          <div className="flex items-center text-green-700 text-sm mt-2">
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            No charge during your {promoDurationDays}-day free trial
          </div>
        )}

        {/* Discounted promo notice */}
        {isDiscounted && promoDiscountPercent && (
          <div className="flex items-center text-green-700 text-sm mt-2">
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            {promoDiscountPercent}% promotional discount applied
          </div>
        )}

        {/* Backend message */}
        {message && (
          <div className="text-xs text-gray-600 mt-2 italic">
            {message}
          </div>
        )}
      </div>

      {/* Billing Schedule */}
      <div className="space-y-3">
        <h4 className="text-sm font-medium text-gray-700 flex items-center">
          <svg className="w-4 h-4 mr-1 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          Billing Schedule
        </h4>

        <div className="bg-white rounded-lg p-3 space-y-2 text-sm">
          {/* Next billing date - most important info */}
          {nextBillingDate && (
            <div className="flex justify-between font-medium">
              <span className="text-gray-700">Next billing date:</span>
              <span className="text-blue-600">{formatDate(nextBillingDate)}</span>
            </div>
          )}

          {/* Next billing amount */}
          {nextBillingAmount > 0 && (
            <div className="flex justify-between">
              <span className="text-gray-600">Amount due on next billing:</span>
              <span className="font-medium">{formatPrice(nextBillingAmount)}</span>
            </div>
          )}

          {/* If in promo, show when regular billing starts */}
          {(isFreeTrial || isDiscounted) && promoEndDate && (
            <div className="flex justify-between text-orange-700 bg-orange-50 -mx-3 px-3 py-1 rounded">
              <span>{isFreeTrial ? 'Trial ends:' : 'Promo ends:'}</span>
              <span className="font-medium">{formatDate(promoEndDate)}</span>
            </div>
          )}

          {/* Downgrade scheduled date */}
          {isDowngrade && scheduledChangeDate && (
            <div className="flex justify-between text-orange-700 bg-orange-50 -mx-3 px-3 py-1 rounded">
              <span>New plan starts:</span>
              <span className="font-medium">{formatDate(scheduledChangeDate)}</span>
            </div>
          )}
        </div>
      </div>

      {/* Recurring Charge Summary */}
      <div className="mt-4 pt-4 border-t border-blue-200">
        <div className="flex justify-between items-center">
          <div>
            <span className="text-sm text-gray-600">Recurring charge</span>
            <span className="text-xs text-gray-500 ml-1">({intervalLabelFull})</span>
          </div>
          <div className="text-right">
            <span className="text-lg font-semibold text-gray-900">{formatPrice(recurringAmount)}</span>
            <span className="text-gray-500 text-sm">/{intervalLabel}</span>
          </div>
        </div>
      </div>

      {/* Confirmation notice for final step */}
      {showConfirmation && (
        <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
          <div className="flex items-start">
            <svg className="w-5 h-5 text-yellow-600 mr-2 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <div className="text-sm">
              <p className="font-medium text-yellow-800">Please confirm your subscription</p>
              <p className="text-yellow-700 mt-1">
                {isDowngrade ? (
                  <>
                    By clicking "Approve", your current plan will continue until {formatDate(currentPlanContinuesUntil)}.
                    Starting {formatDate(scheduledChangeDate)}, you will be charged {formatPrice(recurringAmount)}/{intervalLabel}.
                  </>
                ) : isUpgrade ? (
                  <>
                    By clicking "Approve & Pay", you authorize us to charge {formatPrice(todaysCharge)} today.
                    Your next charge of {formatPrice(nextBillingAmount)} will be on {formatDate(nextBillingDate)},
                    then {formatPrice(recurringAmount)}/{intervalLabel} thereafter.
                  </>
                ) : isFreeTrial ? (
                  <>
                    By clicking "Approve", you start your {promoDurationDays}-day free trial.
                    You will be charged {formatPrice(recurringAmount)}/{intervalLabel} starting {formatDate(promoEndDate || nextBillingDate)}.
                  </>
                ) : (
                  <>
                    By clicking "Approve & Pay", you authorize us to charge {formatPrice(todaysCharge)} today
                    and {formatPrice(recurringAmount)}/{intervalLabel} thereafter.
                  </>
                )}
                {' '}Your subscription will renew automatically until cancelled.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * SubscriptionApprove - Page for approving/declining subscription proposals
 *
 * Requires authentication - user must be logged in and be the designated approver.
 */
const SubscriptionApprove = () => {
  const { labels } = useParadigm();
  const { token } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [subscriptionDetails, setSubscriptionDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [declineReason, setDeclineReason] = useState('');
  const [showDeclineForm, setShowDeclineForm] = useState(false);
  const [success, setSuccess] = useState('');
  const [savedPaymentMethods, setSavedPaymentMethods] = useState([]);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);
  const [showNewCardForm, setShowNewCardForm] = useState(false);

  // Multi-step approval flow
  const [currentStep, setCurrentStep] = useState('review'); // 'review', 'terms', 'billing', 'payment'
  const [legalDocuments, setLegalDocuments] = useState([]);
  const [acceptedDocuments, setAcceptedDocuments] = useState({}); // { documentId: boolean }
  const [billingProfile, setBillingProfile] = useState({
    company_name: '',
    billing_email: '',
    billing_phone: '',
    tax_id: '',
    billing_address: {
      line1: '',
      line2: '',
      city: '',
      state: '',
      postal_code: '',
      country: 'US'
    }
  });
  const [billingErrors, setBillingErrors] = useState({});

  useEffect(() => {
    // Wait for auth to finish loading
    if (authLoading) {
      return;
    }

    // If not authenticated, redirect to login with return URL
    if (!user) {
      const returnUrl = encodeURIComponent(`/subscription/approve/${token}`);
      navigate(`/login?returnUrl=${returnUrl}`);
      return;
    }

    const fetchSubscriptionDetails = async () => {
      try {
        const apiBase = getApiBase();
        const response = await axios.get(
          `${apiBase}/subscriptions/approve/${token}`,
          { headers: getAuthHeaders() }
        );
        setSubscriptionDetails(response.data);

        // Set saved payment methods
        const savedPMs = response.data.saved_payment_methods || [];
        setSavedPaymentMethods(savedPMs);

        // Pre-select default card if available
        if (savedPMs.length > 0) {
          const defaultCard = savedPMs.find(pm => pm.is_default) || savedPMs[0];
          setSelectedPaymentMethod(defaultCard.stripe_payment_method_id);
        }

        // Set billing profile from response
        if (response.data.billing_profile) {
          setBillingProfile(response.data.billing_profile);
        }

        // Set legal documents
        const legalDocs = response.data.legal_documents || [];
        setLegalDocuments(legalDocs);
        // Initialize acceptance state for all documents
        const initialAcceptance = {};
        legalDocs.forEach(doc => {
          initialAcceptance[doc.id] = false;
        });
        setAcceptedDocuments(initialAcceptance);

        setLoading(false);
      } catch (err) {
        const status = err.response?.status;
        if (status === 401) {
          // Token expired or not logged in - redirect to login
          const returnUrl = encodeURIComponent(`/subscription/approve/${token}`);
          navigate(`/login?returnUrl=${returnUrl}`);
        } else if (status === 403) {
          setError('You are not authorized to approve this subscription. Please ensure you are logged in with the correct account.');
        } else if (status === 410) {
          setError('This approval link has expired. Please contact your administrator to resend the approval request.');
        } else {
          setError(err.response?.data?.detail || 'Invalid or expired approval link');
        }
        setLoading(false);
      }
    };

    if (token) {
      fetchSubscriptionDetails();
    } else {
      setError('Invalid approval link');
      setLoading(false);
    }
  }, [token, navigate, user, authLoading]);

  // Check if payment method is required
  const isPaymentRequired = () => {
    const subscription = subscriptionDetails?.subscription;
    if (!subscription) return false;

    // Payment is required for credit card subscriptions that require a payment method
    return subscription.payment_type === 'credit_card' &&
           subscription.promo_requires_payment_method !== false;
  };

  // Validate billing profile
  const validateBillingProfile = () => {
    const errors = {};

    if (!billingProfile.company_name?.trim()) {
      errors.company_name = 'Company name is required';
    }

    if (!billingProfile.billing_email?.trim()) {
      errors.billing_email = 'Billing email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(billingProfile.billing_email)) {
      errors.billing_email = 'Please enter a valid email address';
    }

    if (!billingProfile.billing_address?.line1?.trim()) {
      errors.line1 = 'Street address is required';
    }

    if (!billingProfile.billing_address?.city?.trim()) {
      errors.city = 'City is required';
    }

    if (!billingProfile.billing_address?.postal_code?.trim()) {
      errors.postal_code = 'Postal code is required';
    }

    setBillingErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleReviewContinue = () => {
    // If there are legal documents, go to terms step
    if (legalDocuments.length > 0) {
      setCurrentStep('terms');
    } else {
      setCurrentStep('billing');
    }
  };

  const handleDocumentAcceptanceChange = (documentId, accepted) => {
    setAcceptedDocuments(prev => ({
      ...prev,
      [documentId]: accepted
    }));
  };

  const areAllMandatoryDocumentsAccepted = () => {
    return legalDocuments
      .filter(doc => doc.is_mandatory)
      .every(doc => acceptedDocuments[doc.id]);
  };

  const handleTermsContinue = () => {
    if (!areAllMandatoryDocumentsAccepted()) {
      setError('Please accept all required terms and conditions to continue.');
      return;
    }
    setError('');
    setCurrentStep('billing');
  };

  const handleBillingContinue = () => {
    if (validateBillingProfile()) {
      if (isPaymentRequired()) {
        setCurrentStep('payment');
        // If there are saved cards, don't show new card form by default
        if (savedPaymentMethods.length > 0) {
          setShowNewCardForm(false);
        } else {
          // No saved cards, show new card form
          setShowNewCardForm(true);
          setSelectedPaymentMethod('new');
        }
      } else {
        // Direct approval without payment
        handleApprove(null);
      }
    }
  };

  const handleApprove = async (paymentMethodId) => {
    setIsSubmitting(true);
    setError('');

    try {
      const apiBase = getApiBase();

      // Build legal acceptances array
      const legalAcceptances = legalDocuments
        .filter(doc => acceptedDocuments[doc.id])
        .map(doc => ({
          document_id: doc.id,
          checkbox_text: doc.checkbox_text || `I have read and agree to the ${doc.name}`,
          was_full_document_viewed: true // We show the content in the card
        }));

      await axios.post(
        `${apiBase}/subscriptions/approve/${token}`,
        {
          payment_method_id: paymentMethodId,
          billing_profile: {
            company_name: billingProfile.company_name,
            billing_email: billingProfile.billing_email,
            billing_phone: billingProfile.billing_phone || null,
            tax_id: billingProfile.tax_id || null,
            billing_address: {
              line1: billingProfile.billing_address?.line1 || '',
              line2: billingProfile.billing_address?.line2 || null,
              city: billingProfile.billing_address?.city || '',
              state: billingProfile.billing_address?.state || null,
              postal_code: billingProfile.billing_address?.postal_code || '',
              country: billingProfile.billing_address?.country || 'US'
            }
          },
          legal_acceptances: legalAcceptances.length > 0 ? legalAcceptances : null
        },
        { headers: getAuthHeaders() }
      );

      setSuccess('Subscription approved successfully! Redirecting to billing...');
      setSubscriptionDetails(null);

      // Automatically redirect to billing page after 2 seconds
      setTimeout(() => {
        navigate('/billing');
      }, 2000);
    } catch (err) {
      const detail = err.response?.data?.detail || 'Failed to approve subscription';
      setError(detail);

      // If payment method is required error, show payment form
      if (detail.toLowerCase().includes('payment method')) {
        setCurrentStep('payment');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePaymentSuccess = (paymentMethodId) => {
    // Card collected successfully, now approve with payment method
    handleApprove(paymentMethodId);
  };

  const handleUseSavedCard = () => {
    if (selectedPaymentMethod && selectedPaymentMethod !== 'new') {
      handleApprove(selectedPaymentMethod);
    }
  };

  const handleSelectSavedCard = (paymentMethodId) => {
    setSelectedPaymentMethod(paymentMethodId);
    setShowNewCardForm(false);
  };

  const handleUseNewCard = () => {
    setSelectedPaymentMethod('new');
    setShowNewCardForm(true);
  };

  const handleDecline = async () => {
    setIsSubmitting(true);
    setError('');

    try {
      const apiBase = getApiBase();
      await axios.post(
        `${apiBase}/subscriptions/decline/${token}`,
        null,
        {
          headers: getAuthHeaders(),
          params: { reason: declineReason || undefined }
        }
      );

      setSuccess('Subscription declined. The administrator will be notified.');
      setSubscriptionDetails(null);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to decline subscription');
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatPrice = (cents) => {
    return `$${(cents / 100).toFixed(2)}`;
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-900 to-purple-900 flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg shadow-xl">
          <div className="flex items-center">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mr-3"></div>
            <span>{authLoading ? 'Checking authentication...' : 'Loading subscription details...'}</span>
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-900 to-purple-900 flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg shadow-xl max-w-md w-full mx-4">
          <div className="text-center">
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">Success!</h2>
            <p className="text-gray-600 mb-4">{success}</p>
            <div className="flex items-center justify-center text-gray-500">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
              <span className="text-sm">Redirecting...</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error && !subscriptionDetails) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-900 to-purple-900 flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg shadow-xl max-w-md w-full mx-4">
          <div className="text-center">
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">Unable to Load Subscription</h2>
            <p className="text-gray-600 mb-4">{error}</p>
            <button
              onClick={() => navigate('/billing')}
              className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
            >
              Go to Billing
            </button>
          </div>
        </div>
      </div>
    );
  }

  const subscription = subscriptionDetails?.subscription;
  const organization = subscriptionDetails?.organization;

  // Step indicator
  const renderStepIndicator = () => {
    const steps = [
      { id: 'review', label: 'Review' },
      ...(legalDocuments.length > 0 ? [{ id: 'terms', label: 'Terms' }] : []),
      { id: 'billing', label: 'Billing' },
      ...(isPaymentRequired() ? [{ id: 'payment', label: 'Payment' }] : [])
    ];

    return (
      <div className="flex items-center justify-center mb-6">
        {steps.map((step, index) => (
          <React.Fragment key={step.id}>
            <div className="flex items-center">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  currentStep === step.id
                    ? 'bg-blue-600 text-white'
                    : steps.findIndex(s => s.id === currentStep) > index
                    ? 'bg-green-500 text-white'
                    : 'bg-gray-200 text-gray-600'
                }`}
              >
                {steps.findIndex(s => s.id === currentStep) > index ? (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                ) : (
                  index + 1
                )}
              </div>
              <span className={`ml-2 text-sm ${currentStep === step.id ? 'font-medium text-gray-900' : 'text-gray-500'}`}>
                {step.label}
              </span>
            </div>
            {index < steps.length - 1 && (
              <div className="w-12 h-0.5 bg-gray-200 mx-2" />
            )}
          </React.Fragment>
        ))}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-900 to-purple-900 flex items-center justify-center py-8">
      <div className="bg-white p-8 rounded-lg shadow-xl max-w-lg w-full mx-4">
        <div className="text-center mb-4">
          <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Subscription Approval</h2>
          <p className="text-gray-600">
            Approving subscription for <strong>{organization?.name}</strong>
          </p>
        </div>

        {/* Step Indicator */}
        {!showDeclineForm && renderStepIndicator()}

        {/* Error message */}
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
            {error}
          </div>
        )}

        {/* Step 1: Review */}
        {currentStep === 'review' && !showDeclineForm && (
          <>
            {/* Plan Overview */}
            <div className="bg-gray-50 rounded-lg p-4 mb-4">
              <h3 className="font-semibold text-gray-900 mb-3 flex items-center">
                <svg className="w-5 h-5 mr-2 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                </svg>
                Plan Details
              </h3>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-600">Plan:</span>
                  <span className="font-semibold text-lg">{subscription?.package_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Payment Method:</span>
                  <span className="font-medium capitalize">
                    {subscription?.payment_type === 'credit_card' ? 'Credit Card (Auto-pay)' : 'Invoice (Direct Bill)'}
                  </span>
                </div>
              </div>
            </div>

            {/* Billing Summary - Clear breakdown of charges */}
            <BillingSummary
              subscription={subscription}
              billingSummary={subscriptionDetails?.billing_summary}
              existingSubscription={subscriptionDetails?.existing_subscription}
            />

            {/* Package Entitlements - What's Included */}
            {subscriptionDetails?.package_entitlements && (
              <div className="border border-gray-200 rounded-lg p-4 mb-4 max-h-96 overflow-y-auto">
                <h3 className="font-semibold text-gray-900 mb-3 sticky top-0 bg-white pb-2 flex items-center">
                  <svg className="w-5 h-5 mr-2 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                  </svg>
                  What's Included
                </h3>
                <PackageEntitlements entitlements={subscriptionDetails.package_entitlements} />
              </div>
            )}

            {/* Action buttons */}
            <div className="space-y-3">
              <button
                onClick={handleReviewContinue}
                disabled={isSubmitting}
                className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {legalDocuments.length > 0 ? 'Continue to Terms & Conditions' : 'Continue to Billing Information'}
                <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
              <button
                onClick={() => setShowDeclineForm(true)}
                disabled={isSubmitting}
                className="w-full bg-white text-gray-700 py-3 px-4 rounded-lg font-medium border border-gray-300 hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Decline
              </button>
            </div>

            <p className="mt-4 text-xs text-gray-500 text-center">
              Review the billing summary above carefully. By continuing, you agree to the charges and billing schedule shown.
            </p>
          </>
        )}

        {/* Step 2: Terms & Conditions */}
        {currentStep === 'terms' && !showDeclineForm && (
          <>
            <div className="space-y-4">
              <div className="flex items-center mb-2">
                <svg className="w-5 h-5 text-blue-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <h3 className="font-semibold text-gray-900">Terms & Conditions</h3>
              </div>
              <p className="text-sm text-gray-600 mb-4">
                Please review and accept the following terms to continue with your subscription.
              </p>

              {/* Legal Documents */}
              <div className="space-y-4 max-h-96 overflow-y-auto">
                {legalDocuments.map((doc) => (
                  <LegalDocumentCard
                    key={doc.id}
                    document={doc}
                    accepted={acceptedDocuments[doc.id] || false}
                    onAcceptChange={handleDocumentAcceptanceChange}
                  />
                ))}
              </div>

              {/* Mandatory notice */}
              <p className="text-xs text-gray-500">
                <span className="text-red-500">*</span> Required documents must be accepted to continue.
              </p>
            </div>

            <div className="flex space-x-3 mt-6">
              <button
                type="button"
                onClick={() => setCurrentStep('review')}
                disabled={isSubmitting}
                className="flex-1 bg-white text-gray-700 py-2 px-4 rounded-lg font-medium border border-gray-300 hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                Back
              </button>
              <button
                type="button"
                onClick={handleTermsContinue}
                disabled={isSubmitting || !areAllMandatoryDocumentsAccepted()}
                className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                Continue to Billing
                <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </>
        )}

        {/* Step 3: Billing Profile */}
        {currentStep === 'billing' && !showDeclineForm && (
          <>
            <BillingProfileForm
              billingProfile={billingProfile}
              onChange={setBillingProfile}
              errors={billingErrors}
            />

            <div className="flex space-x-3 mt-6">
              <button
                type="button"
                onClick={() => setCurrentStep(legalDocuments.length > 0 ? 'terms' : 'review')}
                disabled={isSubmitting}
                className="flex-1 bg-white text-gray-700 py-2 px-4 rounded-lg font-medium border border-gray-300 hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                Back
              </button>
              <button
                type="button"
                onClick={handleBillingContinue}
                disabled={isSubmitting}
                className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {isSubmitting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Processing...
                  </>
                ) : isPaymentRequired() ? (
                  <>
                    Continue to Payment
                    <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </>
                ) : (
                  'Approve Subscription'
                )}
              </button>
            </div>
          </>
        )}

        {/* Step 4: Payment */}
        {currentStep === 'payment' && !showDeclineForm && (
          <div className="space-y-4">
            {/* Billing Summary with confirmation */}
            <BillingSummary
              subscription={subscription}
              billingSummary={subscriptionDetails?.billing_summary}
              existingSubscription={subscriptionDetails?.existing_subscription}
              showConfirmation={true}
            />

            {/* Payment Method Selection Header */}
            <div className="flex items-center text-gray-700 mb-2">
              <svg className="w-5 h-5 mr-2 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
              </svg>
              <span className="text-sm font-medium">Select Payment Method</span>
            </div>

            {/* Show saved cards if available */}
            {savedPaymentMethods.length > 0 && (
              <SavedCardSelector
                savedCards={savedPaymentMethods}
                selectedCardId={selectedPaymentMethod}
                onSelect={handleSelectSavedCard}
                onUseNewCard={handleUseNewCard}
              />
            )}

            {/* Show new card form or use saved card button */}
            {showNewCardForm || savedPaymentMethods.length === 0 ? (
              <Elements stripe={stripePromise}>
                <CardEntryForm
                  onSuccess={handlePaymentSuccess}
                  onCancel={() => {
                    if (savedPaymentMethods.length > 0) {
                      setShowNewCardForm(false);
                      const defaultCard = savedPaymentMethods.find(pm => pm.is_default) || savedPaymentMethods[0];
                      setSelectedPaymentMethod(defaultCard.stripe_payment_method_id);
                    } else {
                      setCurrentStep('billing');
                    }
                  }}
                  isSubmitting={isSubmitting}
                  setIsSubmitting={setIsSubmitting}
                  setError={setError}
                />
              </Elements>
            ) : (
              <div className="flex space-x-3">
                <button
                  type="button"
                  onClick={() => setCurrentStep('billing')}
                  disabled={isSubmitting}
                  className="flex-1 bg-white text-gray-700 py-2 px-4 rounded-lg font-medium border border-gray-300 hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  Back
                </button>
                <button
                  type="button"
                  onClick={handleUseSavedCard}
                  disabled={isSubmitting || !selectedPaymentMethod || selectedPaymentMethod === 'new'}
                  className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  {isSubmitting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Processing...
                    </>
                  ) : (
                    'Approve & Pay'
                  )}
                </button>
              </div>
            )}
          </div>
        )}

        {/* Decline form */}
        {showDeclineForm && (
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Reason for declining (optional)
              </label>
              <textarea
                value={declineReason}
                onChange={(e) => setDeclineReason(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                rows={3}
                placeholder="Please provide a reason for declining this subscription..."
              />
            </div>
            <div className="flex space-x-3">
              <button
                onClick={() => setShowDeclineForm(false)}
                disabled={isSubmitting}
                className="flex-1 bg-white text-gray-700 py-2 px-4 rounded-lg font-medium border border-gray-300 hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDecline}
                disabled={isSubmitting}
                className="flex-1 bg-red-600 text-white py-2 px-4 rounded-lg font-medium hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {isSubmitting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Processing...
                  </>
                ) : (
                  'Confirm Decline'
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SubscriptionApprove;
